import type { StationId } from "@/components/where-am-i/registry";

/**
 * Corpus signals used to compute YOU ARE HERE on the Curate tab.
 * Sourced from `usePapersCount(conceptId)` at the overlay mount point.
 */
export interface YouAreHereSignals {
  foundational: number;
  trawled: number;
  scored: number;
}

/**
 * Compute the standing station ("YOU ARE HERE") from current tab + corpus state.
 *
 * State-driven, not history-driven. The previous localStorage-based approach
 * persisted stale `selectedPaper` state across concept switches, writing
 * "review" to fresh concepts' keys even when the user had no papers yet
 * (Codex follow-up smoke 2026-05-28).
 *
 * Logic:
 * - Frame / Analyze / Build tabs → fixed station per tab.
 * - Curate tab → walk the workflow: no seeds → seed, no trawl → trawl,
 *   no scores → score, otherwise → review.
 * - If signals aren't loaded yet, fall back to "trawl" on Curate so the
 *   pill renders something sensible during the first paint.
 */
export function computeYouAreHere(
  currentTab: "Frame" | "Curate" | "Analyze" | "Build",
  signals: YouAreHereSignals | null,
): StationId {
  if (currentTab === "Frame") return "seed";
  if (currentTab === "Analyze") return "analyze";
  if (currentTab === "Build") return "build";
  // Curate — workflow-state aware
  if (!signals) return "trawl";
  if (signals.foundational === 0) return "seed";
  if (signals.trawled === 0) return "trawl";
  if (signals.scored === 0) return "score";
  return "review";
}
